import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';
import { SceneClock } from './lib/scene-clock.js';

// Contrat du rig partagé : interdit. Un seul shader plein écran.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'moire',
  family: 'shader',
  danceability: 0.82,
  requiresTier: 0,
  energyLevel: 0.75,
  bpmMin: 110,
  bpmMax: 160,
  movement: 0.70,
  tonality: 0.50,
  enabled: true
};

// Moire : l'op-art pur. Deux ou trois TRAMES de lignes fines superposées
// (cercles concentriques, lignes parallèles, rayons, spirales, zones de
// Fresnel) ; quand elles glissent ou tournent l'une par rapport à l'autre,
// l'interférence fait naître des FORMES FANTÔMES géantes qui ondulent.
// Preset party de vdjLook (R9 : coupes sur la mesure, flashs autorisés).
// Électronique de 0,6 à 0,95.
//
// UNE TRAME = une fonction de phase φ(x) évaluée dans le shader :
//   cercles φ = |x - c| / pas ; lignes φ = x·dir / pas ; rayons φ = θ n / 2π ;
//   spirale φ = |x - c| / pas + θ k / 2π ; zones φ = |x - c|² / pas².
// La ligne est dessinée EN DISTANCE EN PIXELS (R1) : d = min(fract φ,
// 1 - fract φ) / |∇φ|·px, largeur constante 1,5 à 2,5 px, bord d'un pixel.
// Le gradient est ANALYTIQUE (le brief disait fwidth(φ) : fwidth saute à
// la couture de l'atan des rayons et des spirales et y dessinait une ligne
// grise ; le gradient exact n'a pas de couture et coûte moins).
// ANTI-ALIASING : la période en pixels vaut 1 / (|∇φ| px) ; sous 4 px la
// trame FOND vers sa valeur moyenne (largeur / période, le gris de sa
// couleur) au lieu de produire un moiré d'échantillonnage.
// Deux modes : ADDITIF (chaque trame dans sa couleur vdj sur noir, R3 ; le
// moiré apparaît en couleur mélangée) ou MULTIPLICATIF (lignes sombres sur
// un fond de la couleur de palette, dont la luminance est tenue pour R10).
//
// LES FORMES FANTÔMES : le moiré n'est spectaculaire que si les paramètres
// sont PROCHES. Les écarts sont animés : centres décalés de 2 à 5 pas et
// qui orbitent (nouvelle position sur la mesure, glissée sur un temps) ;
// angle relatif de 1° à 6° qui oscille sur la phrase ; pas différant de 2 à
// 8 %, et bands.low fait respirer celui de la seconde trame de ±3 % (le kick).
//
// RYTHME : temps = le pas pulse (60 ms de montée, retour en 300 ms : les
// franges sautent) et les lignes flashent en blanc sur 1/8 de temps ;
// mesure = nouveaux centres ; phrase = combinaison suivante (fondu croisé
// d'un temps : les deux jeux de trames sont évalués pendant le fondu
// seulement) ; montée = écarts qui tendent vers zéro, les franges
// s'élargissent jusqu'à couvrir l'écran ; drop = rotation brutale de 8° en
// un temps, flash (VdjComfort.requestFlash), inversion pendant une mesure.
//
// CONFORT (R10) : la luminance moyenne est ESTIMÉE analytiquement chaque
// frame (couverture des lignes = largeur / période, par trame, selon le
// mode) et passée à VdjComfort.measure, qui règle l'exposition : aucun
// tampon, aucune relecture.
//
// SCÈNE (R20, lib/scene-clock.js) : une combinaison par scène, cinq
// combinaisons enchaînées ; franges visibles dès la première image. R19 :
// tout est centré sur uStageCenter ; le masque safeZone suit la pochette.
// Tier 0 : deux trames au maximum.
//
// Debug : ?moirecombo=<n>, ?moireoffset=<px> (décalage des centres fixé),
// ?moireangle=<deg> (angle relatif fixé).

const MAX_T = 3;
const SAFE_FLOOR = 0.35;
const SCENE_S = 16;
const KICK_ATTACK = 0.06, KICK_TAU = 0.1;
const FALLBACK = [0x00e5ff, 0xff2bd6, 0xffe14d];
// type: 0 circles, 1 lines, 2 rays, 3 spiral, 4 zones. mode: 0 additive, 1 multiplicative.
// step in height units, n = rays count or spiral turns; off = the centres'
// offset (height units): a few steps - for rays a few PIXELS, the whole
// ghost comes from the two centres almost coinciding
const COMBOS = [
  { mode: 0, off: 0.048, trames: [{ type: 0, step: 0.024 }, { type: 0, step: 0.02436 }] },
  { mode: 1, off: 0.04, trames: [{ type: 1, step: 0.02 }, { type: 1, step: 0.0208 }] },
  { mode: 0, off: 0.01, trames: [{ type: 2, step: 1, n: 72 }, { type: 2, step: 1, n: 75 }, { type: 0, step: 0.03 }] },
  { mode: 0, off: 0.06, trames: [{ type: 3, step: 0.026, n: 3 }, { type: 0, step: 0.0268 }] },
  { mode: 1, off: 0.08, trames: [{ type: 4, step: 0.2 }, { type: 4, step: 0.207 }, { type: 1, step: 0.022 }] }
];
const WIDTHS = [2.0, 1.7, 2.3];

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
const clamp01 = (x) => Math.max(0, Math.min(1, x));
const lerp = (a, b, t) => a + (b - a) * t;

const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

// a trame: uT[i] = (cx, cy, step, angle), uP[i] = (type, n, width px, colour index); two sets A and B
const fragment = (maxT) => `
  precision highp float;
  uniform float uAspect, uPx;
  uniform vec2 uStage;
  uniform vec4 uTA[${maxT}], uPA[${maxT}];
  uniform vec4 uTB[${maxT}], uPB[${maxT}];
  uniform float uNA, uNB, uMix, uModeA, uModeB;
  uniform vec3 uCol0, uCol1, uCol2, uBg;
  uniform float uFlash, uInvert, uExposure, uTime;
  uniform vec2 uSafeCenter, uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  float hash2(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  vec3 colourOf(float k) { return k < 0.5 ? uCol0 : (k < 1.5 ? uCol1 : uCol2); }
  // the phase and its gradient's norm (height units^-1), ANALYTIC: fwidth()
  // spikes across the atan seam of rays and spirals and drew a grey line
  // there; the exact gradient has no seam and costs less
  vec2 phaseOf(vec2 x, vec4 T, vec4 P) {
    vec2 q = x - T.xy;
    float ca = cos(T.w), sa = sin(T.w);
    q = vec2(ca * q.x - sa * q.y, sa * q.x + ca * q.y);
    float type = P.x, inv = 1.0 / T.z;
    float r = max(length(q), 1e-5);
    if (type < 0.5) return vec2(r * inv, inv);
    if (type < 1.5) return vec2(q.y * inv, inv);
    float ang = atan(q.y, q.x), kr = P.y / 6.2831853;
    if (type < 2.5) return vec2(ang * kr, kr / r);
    if (type < 3.5) return vec2(r * inv + ang * kr, sqrt(inv * inv + kr * kr / (r * r)));
    return vec2(dot(q, q) * inv * inv, 2.0 * r * inv * inv);
  }
  // the line's coverage (0..1) and, when the period drops under 4 px, the fade to the mean
  float trame(vec2 x, vec4 T, vec4 P) {
    vec2 pg = phaseOf(x, T, P);
    float fw = max(pg.y * uPx * 1.15, 1e-6);          // phase per pixel (1.15: the L1 norm fwidth would see)
    float period = 1.0 / fw;                          // in pixels
    float f = fract(pg.x);
    float d = min(f, 1.0 - f) / fw;                   // pixels to the nearest line
    float w = P.z;
    float line = 1.0 - smoothstep(0.5 * w - 0.5, 0.5 * w + 0.5, d);
    float duty = clamp(w / period, 0.0, 1.0);
    return mix(duty, line, smoothstep(2.0, 4.0, period));
  }
  vec3 combo(vec2 x, vec4 T[${maxT}], vec4 P[${maxT}], float n, float mode) {
    vec3 col = vec3(0.0);
    if (mode < 0.5) {
      for (int i = 0; i < ${maxT}; i++) {
        if (float(i) >= n) break;
        float v = trame(x, T[i], P[i]);
        col += mix(colourOf(P[i].w), vec3(1.0), uFlash) * v;
      }
    } else {
      // dark lines over the palette background
      float k = 1.0;
      for (int i = 0; i < ${maxT}; i++) {
        if (float(i) >= n) break;
        k *= 1.0 - 0.92 * trame(x, T[i], P[i]);
      }
      col = mix(uBg, vec3(1.0), uFlash * 0.5) * k;
    }
    return col;
  }
  void main() {
    vec2 x = (vUv - 0.5) * vec2(uAspect, 1.0) - uStage;
    vec3 col = combo(x, uTA, uPA, uNA, uModeA);
    if (uMix > 0.001) col = mix(col, combo(x, uTB, uPB, uNB, uModeB), uMix);
    // the drop's inversion: the complement, dimmed (R10)
    col = mix(col, (vec3(1.0) - col) * 0.55, uInvert);
    col *= uExposure;
    // a soft knee under the bloom threshold (0.78): only the flash's white cores cross it
    float L = dot(col, vec3(0.2126, 0.7152, 0.0722));
    // ALWAYS: a frame-wide grid of white 2 px lines over the threshold blooms
    // into a uniform white veil (measured: mean 0.92). The flash stays a flash
    // of the lines, not of the frame.
    float k = L > 0.6 ? (0.6 + 0.14 * (1.0 - exp(-(L - 0.6) / 0.14))) / L : 1.0;
    col *= k;
    // the cover's mask (R19: it follows the cover)
    vec2 s2 = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec2 q = abs(s2 - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    col *= mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, sd));
    col += (hash2(gl_FragCoord.xy + fract(uTime)) - 0.5) / 255.0;
    gl_FragColor = vec4(max(col, 0.0), 1.0);
  }
`;

const MATERIALS = new Map();

export class MoireVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._maxT = this._tier === 0 ? 2 : MAX_T;
    const c = parseInt(urlParam('moirecombo'), 10); this._forceCombo = Number.isFinite(c) ? ((c % COMBOS.length) + COMBOS.length) % COMBOS.length : null;
    const off = parseFloat(urlParam('moireoffset')); this._forceOffset = Number.isFinite(off) ? off : null;
    const ang = parseFloat(urlParam('moireangle')); this._forceAngle = Number.isFinite(ang) ? ang * Math.PI / 180 : null;
    this._post = new VdjPost({ bloomStrength: 0.55, bloomThreshold: 0.78, grain: 0.0, vignette: 0.35, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort();
    this._clock = new SceneClock({ period: SCENE_S, start: 'random' });
  }

  get postProcessing() { return this._post.value; }
  _rnd() { this._seed = (this._seed * 16807) % 2147483647; return this._seed / 2147483647; }

  init(scene) {
    this._scene = scene;
    this._seed = 4242;
    const key = 'moire:' + this._maxT;
    let mat = MATERIALS.get(key);
    const vec4s = () => { const a = []; for (let i = 0; i < this._maxT; i++) a.push(new THREE.Vector4()); return a; };
    if (!mat) {
      mat = new THREE.ShaderMaterial({
        uniforms: {
          uAspect: safeZoneUniforms.uAspect, uPx: { value: 1 / 1080 }, uStage: { value: new THREE.Vector2() },
          uTA: { value: vec4s() }, uPA: { value: vec4s() }, uTB: { value: vec4s() }, uPB: { value: vec4s() },
          uNA: { value: 0 }, uNB: { value: 0 }, uMix: { value: 0 }, uModeA: { value: 0 }, uModeB: { value: 0 },
          uCol0: { value: new THREE.Color() }, uCol1: { value: new THREE.Color() }, uCol2: { value: new THREE.Color() }, uBg: { value: new THREE.Color() },
          uFlash: { value: 0 }, uInvert: { value: 0 }, uExposure: { value: 1 }, uTime: { value: 0 },
          uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
        },
        vertexShader: quadVertex, fragmentShader: fragment(this._maxT), depthTest: false, depthWrite: false
      });
      MATERIALS.set(key, mat);
    }
    this._mat = mat; this._u = mat.uniforms;
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._size = new THREE.Vector2();
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._display);

    this._time = 0; this._free = 0; this._beats = 0; this._bars = 0; this._secondsBeat = 60 / 128;
    this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null;
    this._low = 0.3; this._build = 0; this._kickAge = 9; this._beatPhase = 0;
    this._combo = this._forceCombo != null ? this._forceCombo : Math.floor(this._rnd() * COMBOS.length);
    this._prevCombo = this._combo; this._mix = 0; this._mixing = false;
    this._orbit = this._rnd() * Math.PI * 2; this._orbitTarget = this._orbit; this._orbitFrom = this._orbit; this._glide = 1;
    this._dropAngle = 0; this._dropAngleTarget = 0; this._invertUntil = -1;
    this._paletteRef = undefined; this._palette = FALLBACK.slice();
    this._applyPalette();
    this._exposure = 1;
    this._apply();
  }

  _applyPalette() {
    const v = this._palette;
    vdjColor(v[0], this._u.uCol0.value);
    vdjColor(v[1 % v.length], this._u.uCol1.value);
    // the third colour: the palette's third, or the complement of the first
    if (v.length > 2) vdjColor(v[2], this._u.uCol2.value);
    else { const h = { h: 0, s: 0, l: 0 }; this._u.uCol0.value.getHSL(h); this._u.uCol2.value.setHSL((h.h + 0.5) % 1, 0.9, 0.55); }
    // the multiplicative background: the first colour, dimmed to a luminance of 0.42 (R10)
    const bg = this._u.uBg.value.copy(this._u.uCol0.value);
    const L = 0.2126 * bg.r + 0.7152 * bg.g + 0.0722 * bg.b;
    bg.multiplyScalar(0.42 / Math.max(L, 1e-3));
    bg.r = Math.min(bg.r, 1); bg.g = Math.min(bg.g, 1); bg.b = Math.min(bg.b, 1);
  }

  // the trames of a combo at the current offsets, into set T/P; returns the estimated mean luminance
  _setCombo(idx, T, P, which) {
    const c = COMBOS[idx], n = Math.min(c.trames.length, this._maxT);
    const u = this._u;
    const H = this._size.y || 1080;
    const gap = 1 - 0.85 * this._build;                 // the build closes every gap
    const kickEnv = this._kickAge < KICK_ATTACK ? this._kickAge / KICK_ATTACK : Math.exp(-(this._kickAge - KICK_ATTACK) / KICK_TAU);
    const pulse = 1 + 0.04 * kickEnv;
    const stepRef = c.trames[0].step;
    const offPx = this._forceOffset != null ? this._forceOffset : (c.off * H) * gap;
    const relAng = this._forceAngle != null ? this._forceAngle : ((2.5 + 3.5 * Math.sin(this._beatsCont * Math.PI * 2 / 64)) * Math.PI / 180) * gap + this._dropAngle;
    let lum = 0, mult = 1;
    for (let i = 0; i < n; i++) {
      const t = c.trames[i];
      const a = this._orbit + i * 2.1;
      const r = (i === 0 ? 0 : offPx / H) * (i === 2 ? 0.6 : 1);
      // the second trame's step breathes on the kick (bands.low): +-3 %
      let step = t.step * pulse * (1 + (i === 1 ? 0.03 * (this._low * 2 - 1) : 0));
      // the build closes the step ratio too - between trames of the SAME type only
      // (a line trame measured against a zone plate's step shrank to 1.6 px)
      if (i > 0 && t.type === c.trames[0].type) step = stepRef * pulse * (1 + (t.step / stepRef - 1) * gap) * (step / (t.step * pulse));
      const cx = Math.cos(a) * r, cy = Math.sin(a) * r;
      T[i].set(cx, cy, step, (i === 0 ? 0 : relAng * (i === 2 ? -0.7 : 1)) + this._orbit * 0.05);
      P[i].set(t.type, t.n || 0, WIDTHS[i], i);
      // the estimate: coverage = width / period (px); rays/spiral: the period at r = 0.3
      const periodPx = t.type === 2 ? 2 * Math.PI * 0.3 * H / t.n : t.type === 4 ? 0.5 * step * step / 0.3 * H : step * H;
      const duty = Math.min(1, WIDTHS[i] / Math.max(periodPx, 1));
      const col = i === 0 ? u.uCol0.value : i === 1 ? u.uCol1.value : u.uCol2.value;
      lum += duty * (0.2126 * col.r + 0.7152 * col.g + 0.0722 * col.b);
      mult *= 1 - 0.92 * duty;
    }
    for (let i = n; i < this._maxT; i++) P[i].set(0, 0, 0, 0);
    if (which === 'A') { u.uNA.value = n; u.uModeA.value = c.mode; } else { u.uNB.value = n; u.uModeB.value = c.mode; }
    return c.mode === 0 ? lum : 0.42 * mult;
  }

  _apply() {
    const u = this._u;
    const lA = this._setCombo(this._combo, u.uTA.value, u.uPA.value, 'A');
    let est = lA;
    if (this._mixing) { const lB = this._setCombo(this._prevCombo, u.uTB.value, u.uPB.value, 'B'); est = lerp(lA, lB, this._mix); }
    u.uMix.value = this._mixing ? this._mix : 0;
    return est;
  }

  update(deltaTime, profile) {
    if (profile.artworkPalette !== this._paletteRef) {
      this._paletteRef = profile.artworkPalette;
      const v = profile.artworkPalette && profile.artworkPalette.vividColors && profile.artworkPalette.vividColors.length ? profile.artworkPalette.vividColors : FALLBACK;
      this._palette = v.slice(0, 3);
      this._applyPalette();
    }
    const playing = profile.playbackState === undefined || profile.playbackState === 'PLAYING';
    const dt = playing ? Math.min(deltaTime, 0.1) : 0;
    this._clock.update(dt);
    if (dt === 0) return;                         // paused: frozen
    this._time += dt;
    const director = profile.director;
    const bands = (director && director.hasReliableGrid && director.bands) ? director.bands : null;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    this._secondsBeat = bpm > 0 ? 60 / bpm : 60 / 128;
    const barS = 4 * this._secondsBeat;
    // the grid (or a free clock at 128 BPM)
    let beat = false, bar = false, phrase = false;
    const bp = profile.beatPhase;
    if (bp != null) { if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) beat = true; this._phaseBeat = bp; this._beatPhase = bp; }
    else { this._free += dt; if (this._free >= this._secondsBeat) { this._free -= this._secondsBeat; beat = true; } this._beatPhase = this._free / this._secondsBeat; }
    const barPhase = profile.barPhase;
    if (barPhase != null) { if (this._phaseBar != null && barPhase < this._phaseBar - 0.5) bar = true; this._phaseBar = barPhase; }
    else if (beat && (this._beats + 1) % 4 === 0) bar = true;
    const ph = profile.phrasePhase;
    if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
    else if (beat && (this._beats + 1) % 16 === 0) phrase = true;
    if (beat) this._beats++;
    if (bar) this._bars++;
    this._beatsCont = this._beats + this._beatPhase;
    // the kick: bands.low, or a synthetic one from the beat phase
    const low = this._lowForce != null ? this._lowForce : bands ? clamp01(bands.low) : Math.exp(-this._beatPhase * 6);
    this._low += (low - this._low) * Math.min(1, dt / 0.05);

    // the build: a known drop approaching, or the 'build' segment
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && director.mode === 'build') build = Math.max(build, 0.6);
    this._build += (build - this._build) * (1 - Math.exp(-dt / 0.8));

    // DROP: +8 deg in one beat, a flash, the inversion for a bar
    const dropNow = next && next.type === 'drop' && next.msUntil != null && next.msUntil <= dt * 500 + 30 && profile.estimatedPosition != null;
    if (dropNow) {
      const key = Math.round((profile.estimatedPosition * 1000 + next.msUntil) / 100);
      if (key !== this._lastDropKey) {
        this._lastDropKey = key;
        this._dropAngleTarget += 8 * Math.PI / 180;
        this._invertUntil = this._time + barS;
        if (director && this._comfort.requestFlash(this._time)) director.flash = VDJ_DROP_FLASH;
      }
    }
    this._dropAngle += (this._dropAngleTarget - this._dropAngle) * Math.min(1, dt / (this._secondsBeat * 0.5));

    // BEAT: the step pulses, the lines flash white for 1/8 beat
    if (beat) this._kickAge = 0;
    this._kickAge += dt;
    this._u.uFlash.value = this._beatPhase < 0.125 ? 1 - this._beatPhase / 0.125 : 0;
    // BAR: new centres, glided over one beat
    if (bar) { this._orbitFrom = this._orbit; this._orbitTarget += 0.6 + 0.9 * this._rnd(); this._glide = 0; }
    this._glide = Math.min(1, this._glide + dt / this._secondsBeat);
    const g = this._glide * this._glide * (3 - 2 * this._glide);
    this._orbit = lerp(this._orbitFrom, this._orbitTarget, g);
    // PHRASE (or the scene clock wrapping): the next combination, crossfaded over one beat
    if ((phrase || this._clock.wrapped) && this._forceCombo == null && !this._mixing) {
      this._prevCombo = this._combo; this._combo = (this._combo + 1) % COMBOS.length; this._mixing = true; this._mix = 1;
    }
    if (this._mixing) { this._mix -= dt / this._secondsBeat; if (this._mix <= 0) { this._mix = 0; this._mixing = false; } }

    this._u.uInvert.value = this._time < this._invertUntil ? 1 : 0;
    this._u.uTime.value = this._time % 1000;
    const est = this._apply();
    // comfort: the analytic estimate, after the current gain, feeds the governor
    const gain = this._comfort.measure(est * this._exposure * (this._u.uInvert.value > 0.5 ? 1.2 : 1), dt);
    this._exposure = gain;
    this._u.uExposure.value = gain;
    this._post.update(vdjBeatChroma(this._beatPhase, this._secondsBeat));
  }

  _render(renderer) {
    renderer.getDrawingBufferSize(this._size);
    const st = safeZoneUniforms.uStageCenter.value;
    this._u.uStage.value.set(st.x * 0.5, st.y * 0.5);
    this._u.uPx.value = 1 / Math.max(1, this._size.y);
  }

  get probe() {
    return { t: +this._time.toFixed(2), combo: this._combo, mix: +this._mix.toFixed(2), orbit: +this._orbit.toFixed(2), low: +this._low.toFixed(2),
      build: +this._build.toFixed(2), exposure: +this._exposure.toFixed(3), flash: this._u.uFlash.value, invert: this._u.uInvert.value, beats: this._beats,
      TA: this._u.uTA.value.slice(0, this._u.uNA.value).map((v) => v.toArray().map((x) => +x.toFixed(4))) };
  }

  dispose() {
    if (this._display) { this._display.onBeforeRender = null; if (this._scene) this._scene.remove(this._display); this._display.geometry.dispose(); }
    this._scene = null;                           // the material is shared (cached), not disposed
  }
}
